import {resource} from '@albert-io/json-api-framework/request/builder';

import sessionStore from 'client/Session/SessionStore';
import {getBaseUserQuery} from 'client/User/UserQueryUtil';

import masqueradeStore from './Masquerade.store';

/**
 *
 * @returns {Promise<Immutable.List>}
 */
export async function getTeacherApprovedPersonnelRecordsWithMasqueradeState() {
  /**
   * If the requesting user is `super` or matches the user we can make a more basic request for these records since
   * we won't hit permission issues.
   */
  if (!masqueradeStore.isMasquerading() || sessionStore.isSuper()) {
    const records = await resource('school_personnel_v1')
      .mandarkEndpoint([
        'teachers_v1',
        masqueradeStore.getUserIdByMasqueradeState(),
        'school_personnel_v1'
      ])
      .include('school_v5')
      .filter({
        status: 'approved'
      })
      .pageSize(50)
      .getResourcePromise();
    return records;
  }
  /**
   * The user is masquerading and will need an altered request to ensure we don't
   * overstep permissions (a school administrator can only see the personnel records for the school(s) they are
   * associated with).
   */

  /**
   * Get the school personnel records the user is an approved administrator for.
   */
  const administratingFor = await resource('school_personnel_v1')
    .mandarkEndpoint(['teachers_v1', sessionStore.getUserId(), 'school_personnel_v1'])
    .include('school_v5')
    .include('role_v1')
    .filter({
      role_v1: {
        name: {
          not: 'teacher'
        }
      }
    })
    .getResourcePromise();

  const schoolIds = administratingFor.map((record) => record.getSchool().getId()).join(',');

  const teacherRecords = await resource('school_personnel_v1')
    .mandarkEndpoint([
      'teachers_v1',
      masqueradeStore.getUserIdByMasqueradeState(),
      'school_personnel_v1'
    ])
    .include('school_v5')
    .filter({
      status: 'approved',
      /**
       * IMPORTANT: We are filtering the personnel records by the schools the administrator belongs to.
       */
      school_v5: schoolIds
    })
    .pageSize(50)
    .getResourcePromise();

  return teacherRecords;
}

/**
 * Returns teacher's schools formatted to be used in `<Select>` component
 *
 * @param {*} teacherId The ID of the teacher to lookup.
 * @returns {Array<{value: string, label: string}>} Teacher's schools formatted for use in
 */
export async function getSchoolOptionsWithMasqueradeState(teacherId) {
  /**
   * We're storing values as an object while we build out the options, but eventually end up returning the values as an
   * array via Object.values. This is because a user may have multiple approved records for a given school. Rather than
   * iterate through each record and check if it has already been processed, using an Object lets us just overwrite previous
   * entries.
   */
  const initialValues = {
    default: {
      value: '',
      label: 'Choose...'
    }
  };
  /**
   * We only add the school associated to the user account if the user is *NOT* masquerading OR
   * if it's a `super` user... this ensures we avoid permission issues for school administrators
   * attempting to do user lookups on a teachers `user_v*` record.
   */
  if (sessionStore.isSuper() || !masqueradeStore.isMasquerading()) {
    const user = await getBaseUserQuery(teacherId).getResourcePromise();
    initialValues[user.getSchool().getId()] = {
      value: user.getSchool().getId(),
      label: user.getSchool().getName()
    };
  }

  const records = await getTeacherApprovedPersonnelRecordsWithMasqueradeState();
  if (records.isEmpty()) {
    return Object.values(initialValues);
  }

  const valuesObj = records.reduce((acc, personnelRecord) => {
    const school = personnelRecord.getSchool();
    const schoolId = school.getId();

    acc[schoolId] = {
      value: schoolId,
      label: school.getName()
    };

    return acc;
  }, initialValues);

  return Object.values(valuesObj);
}
